import React from "react";
import { Link } from "react-router-dom";
import {
  MapPin,
  Clock,
  Briefcase,
  CheckCircle,
  ArrowRight,
  Phone,
  Award,
} from "lucide-react";

const PhysiotherapistJob = () => {
  const breadcrumbs = [
    { label: "Home", link: "/" },
    { label: "Careers", link: "/careers" },
    { label: "Physiotherapist", link: null },
  ];

  const keyResponsibilities = [
    "Conduct comprehensive assessments of clients' physical function and mobility needs",
    "Develop and implement evidence-based treatment plans tailored to individual goals",
    "Provide therapeutic interventions to improve strength, balance, mobility, and function",
    "Prescribe and review assistive devices and mobility aids",
    "Educate clients and families on exercise programs and self-management strategies",
    "Collaborate with multidisciplinary teams including doctors, nurses, and occupational therapists",
    "Maintain accurate clinical documentation and progress reports",
    "Participate in case conferences and care planning meetings",
    "Ensure compliance with AHPRA standards and NDIS quality framework",
  ];

  const essentialRequirements = [
    "Current registration with AHPRA (Australian Health Practitioner Regulation Agency)",
    "Bachelor's or Master's degree in Physiotherapy from an accredited institution",
    "Strong clinical assessment and treatment skills",
    "Excellent communication and interpersonal abilities",
    "Ability to work autonomously and as part of a multidisciplinary team",
    "Current Professional Indemnity Insurance",
    "Current NDIS Worker Screening Check or willingness to obtain",
    "Valid NSW Driver's License and willingness to travel to client homes",
    "Current First Aid and CPR certification",
  ];

  const desirableQualifications = [
    "Experience in aged care, disability, or community-based physiotherapy",
    "Post-graduate qualifications in gerontology, neurology, or rehabilitation",
    "Experience with NDIS service delivery and documentation",
    "Knowledge of assistive technology and equipment prescription",
    "Manual therapy and exercise prescription skills",
    "Experience in falls prevention and balance training programs",
    "Understanding of chronic disease management in older adults",
  ];

  const benefits = [
    "Competitive salary commensurate with experience and qualifications",
    "Salary packaging options available to maximize take-home pay",
    "Flexible working arrangements including part-time and full-time options",
    "Professional development support and continuing education opportunities",
    "Access to modern equipment and clinical resources",
    "Supportive team environment with regular clinical supervision",
    "Vehicle allowance for community visits",
    "Opportunity to make a genuine impact in your local community",
  ];

  return (
    <div className="min-h-screen bg-white">
      <section
        className="py-12 px-6 md:px-12 lg:px-24"
        style={{ backgroundColor: "#F5F0F1" }}
      >
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-2 text-sm text-gray-600 mb-6">
            {breadcrumbs.map((crumb, index) => (
              <React.Fragment key={index}>
                {index > 0 && <span>|</span>}
                {crumb.link ? (
                  <Link
                    to={crumb.link}
                    className="hover:text-[#E85D2C] transition-colors"
                  >
                    {crumb.label}
                  </Link>
                ) : (
                  <span className="text-gray-900">{crumb.label}</span>
                )}
              </React.Fragment>
            ))}
          </div>

          <div className="space-y-6">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
              Physiotherapist
            </h1>

            <div className="flex flex-wrap gap-4 text-sm">
              <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-lg">
                <MapPin size={16} className="text-[#E85D2C]" />
                <span className="text-gray-700">Coolamon, NSW 2701</span>
              </div>
              <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-lg">
                <Clock size={16} className="text-[#E85D2C]" />
                <span className="text-gray-700">Full-Time | Part-Time</span>
              </div>
              <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-lg">
                <Briefcase size={16} className="text-[#E85D2C]" />
                <span className="text-gray-700">Allied Health</span>
              </div>
              <div className="flex items-center gap-2 bg-[#E85D2C] text-white px-4 py-2 rounded-lg">
                <Award size={16} />
                <span className="font-medium">AHPRA Registration Required</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-24 px-6 md:px-12 lg:px-24">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-3 gap-12">
            <div className="lg:col-span-2 space-y-12">
              <div className="space-y-6">
                <h2 className="text-3xl font-bold text-gray-900">
                  About Allawah Lodge
                </h2>
                <p className="text-base text-gray-700 font-light leading-relaxed">
                  Allawah Lodge is a leading provider of quality care services
                  in Coolamon, NSW. We deliver professional, person-centred
                  support to older Australians and people with disabilities,
                  helping them maintain independence and enhance their quality
                  of life. Our multidisciplinary team works collaboratively to
                  provide holistic care that addresses physical, emotional, and
                  social wellbeing.
                </p>
              </div>

              <div className="space-y-6">
                <h2 className="text-3xl font-bold text-gray-900">
                  Role Overview
                </h2>
                <p className="text-base text-gray-700 font-light leading-relaxed">
                  We are seeking a qualified and passionate Physiotherapist to
                  join our allied health team. In this role, you will provide
                  professional physiotherapy services to clients in their homes
                  and community settings throughout the Coolamon region. You
                  will work with a diverse client base including older adults,
                  people with disabilities, and those recovering from injury or
                  illness.
                </p>
                <p className="text-base text-gray-700 font-light leading-relaxed">
                  This is an excellent opportunity for a physiotherapist who
                  values autonomy, professional growth, and making a tangible
                  difference in people's lives. You will have the flexibility to
                  manage your own caseload while being supported by an
                  experienced multidisciplinary team.
                </p>
              </div>

              <div className="space-y-6">
                <h2 className="text-3xl font-bold text-gray-900">
                  Key Responsibilities
                </h2>
                <ul className="space-y-3">
                  {keyResponsibilities.map((responsibility, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <CheckCircle
                        size={16}
                        className="text-[#E85D2C] flex-shrink-0 mt-1"
                      />
                      <span className="text-sm text-gray-700 font-light leading-relaxed">
                        {responsibility}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="space-y-6">
                <h2 className="text-3xl font-bold text-gray-900">
                  Essential Requirements
                </h2>
                <ul className="space-y-3">
                  {essentialRequirements.map((requirement, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <CheckCircle
                        size={16}
                        className="text-[#E85D2C] flex-shrink-0 mt-1"
                      />
                      <span className="text-sm text-gray-700 font-light leading-relaxed">
                        {requirement}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="space-y-6">
                <h2 className="text-3xl font-bold text-gray-900">
                  Desirable Qualifications and Experience
                </h2>
                <ul className="space-y-3">
                  {desirableQualifications.map((qualification, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <CheckCircle
                        size={16}
                        className="text-[#E85D2C] flex-shrink-0 mt-1"
                      />
                      <span className="text-sm text-gray-700 font-light leading-relaxed">
                        {qualification}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="space-y-6">
                <h2 className="text-3xl font-bold text-gray-900">
                  What We Offer
                </h2>
                <ul className="space-y-3">
                  {benefits.map((benefit, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <CheckCircle
                        size={16}
                        className="text-[#E85D2C] flex-shrink-0 mt-1"
                      />
                      <span className="text-sm text-gray-700 font-light leading-relaxed">
                        {benefit}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-gray-50 rounded-2xl p-8 space-y-4">
                <h2 className="text-2xl font-bold text-gray-900">
                  Equal Opportunity Employer
                </h2>
                <p className="text-base text-gray-700 font-light leading-relaxed">
                  Allawah Lodge is an equal opportunity employer. We welcome
                  applications from all suitably qualified candidates regardless
                  of age, gender, cultural background, disability, sexual
                  orientation, or religious beliefs. We are committed to
                  creating an inclusive workplace that reflects the diversity of
                  our community.
                </p>
              </div>
            </div>

            <div className="lg:col-span-1">
              <div className="sticky top-8 space-y-6">
                <div className="bg-[#E85D2C] rounded-2xl p-8 space-y-6">
                  <h3 className="text-2xl font-bold text-white">
                    Ready to Apply?
                  </h3>

                  <p className="text-base text-white/90 font-light leading-relaxed">
                    Join our allied health team and help clients achieve their
                    mobility and independence goals.
                  </p>

                  <div className="space-y-4">
                    <Link
                      to="/jobs/physiotherapist/apply"
                      className="flex items-center justify-center gap-2 bg-white hover:bg-gray-100 text-[#E85D2C] font-medium px-8 py-4 rounded-lg transition-all duration-300 w-full"
                    >
                      Apply Now
                      <ArrowRight size={18} />
                    </Link>

                    <p className="text-xs text-white/70 text-center font-light">
                      Applications are reviewed on a rolling basis
                    </p>
                  </div>

                  <div className="pt-4 border-t border-white/20 space-y-2">
                    <p className="text-sm text-white/90 font-light">
                      <strong className="font-medium">Note:</strong> Shortlisted
                      candidates will be contacted for interview. AHPRA
                      registration verification and reference checks form part
                      of our recruitment process.
                    </p>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-2xl p-8 space-y-4">
                  <h3 className="text-xl font-bold text-gray-900">
                    Questions?
                  </h3>

                  <p className="text-sm text-gray-700 font-light leading-relaxed">
                    If you have any questions about this role, please contact
                    our HR team.
                  </p>

                  <a
                    href="tel:+61269272706"
                    className="inline-flex items-center gap-2 text-[#E85D2C] hover:text-[#d54d1f] font-medium transition-colors text-sm"
                  >
                    <Phone size={16} />
                    (02) 6927 2706
                  </a>

                  <Link
                    to="/contact"
                    className="inline-flex items-center gap-2 text-[#E85D2C] hover:text-[#d54d1f] font-medium transition-colors text-sm block mt-2"
                  >
                    Contact Us
                    <ArrowRight size={16} />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-[#E85D2C] text-white py-16 lg:py-24 px-6 md:px-12 lg:px-24">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-4xl mx-auto text-center space-y-8">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold leading-tight">
              Explore Other Career Opportunities
            </h2>

            <Link
              to="/careers"
              className="inline-flex items-center gap-2 bg-white hover:bg-gray-100 text-[#E85D2C] font-medium px-8 py-4 rounded-lg transition-all duration-300"
            >
              View All Positions
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PhysiotherapistJob;
